import firebase from "firebase";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps } from "react-router";
import { bindActionCreators, Dispatch } from "redux";
import styled from "styled-components";
import { makeId, validateVenueSpace, VenueSpace } from "sweet-global";
import BackButton from "sweet-web/components/universal/Button/BackButton";
import NextButton from "sweet-web/components/universal/Button/NextButton";
import OutlineButton from "sweet-web/components/universal/Button/OutlineButton";
import { Label, SectionTitle } from "sweet-web/components/universal/Text";
import {
  OptionWrapper,
  Section,
  Wrapper
} from "sweet-web/components/universal/View";
import { RootState } from "sweet-web/redux";
import * as selectors from "sweet-web/redux/selectors";
import { UpdateVenue } from "sweet-web/redux/venue/actions";
import EditSpace from "./EditSpace";
import SpaceLine from "./SpaceLine";

type Props = RouteComponentProps & ConnectedProps<typeof connector>;

const Spaces: React.FC<Props> = props => {
  const { venueId } = props.match.params as { venueId: string };
  const { venueEntity, loading } = props;
  const [addingSpace, setAddingSpace] = useState(false);
  const [editingSpaces, setEditingSpaces] = useState([]);
  const [venue, setVenue] = useState(venueEntity);
  useEffect(() => {
    setVenue(venueEntity);
  }, [venueEntity]);

  const addSpace = async (space: VenueSpace) => {
    const newId = makeId();

    return await saveSpace(newId, space);
  };

  const saveSpace = async (
    spaceId: string,
    space: VenueSpace
  ): Promise<boolean> => {
    const validation = validateVenueSpace(space);
    if (!validation.valid) {
      alert(`Please complete ${validation.errors.join(", ")}`);

      return false;
    }
    setVenue(v => ({
      ...v,
      spaces: {
        ...v.spaces,
        [spaceId]: space
      }
    }));
    await props.UpdateVenue(venueId, { [`spaces.${spaceId}`]: space });

    return true;
  };

  const removeSpace = async (spaceId: string) => {
    await props.UpdateVenue(venueId, {
      [`spaces.${spaceId}`]: firebase.firestore.FieldValue.delete()
    });
  };

  const next = async () => {
    props.history.push(`/listing/${venueId}/services`);
  };
  const back = async () => {
    props.history.push(`/listing/${venueId}`);
  };

  return (
    <Wrapper>
      <Section>
        <SectionTitle style={{ marginBottom: 5 }}>Space Options</SectionTitle>
        <Label style={{ marginBottom: 20 }}>
          Outline the different spaces in your venue that can be booked (eg.
          specific room, entire bar)
        </Label>

        {Object.keys(venue.spaces).length > 0 && (
          <OptionWrapper>
            {Object.keys(venue.spaces).map(spaceId => {
              const space = venue.spaces[spaceId];
              if (editingSpaces.includes(spaceId))
                return (
                  <EditSpace
                    key={`edit-space-${spaceId}`}
                    spaceId={spaceId}
                    spaceEntity={space}
                    onSave={async revisedSpace => {
                      const saved = await saveSpace(spaceId, revisedSpace);
                      if (saved)
                        setEditingSpaces(e => _.remove(e, { i: spaceId }));
                    }}
                    onCancel={() =>
                      setEditingSpaces(e => _.remove(e, { i: spaceId }))
                    }
                  />
                );
              else
                return (
                  <SpaceLine
                    key={`view-space-${spaceId}`}
                    spaceId={spaceId}
                    spaceEntity={space}
                    onEdit={() => setEditingSpaces(e => [...e, spaceId])}
                    onRemove={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to remove this space?"
                        )
                      ) {
                        removeSpace(spaceId);
                      }
                    }}
                  />
                );
            })}
          </OptionWrapper>
        )}

        {!addingSpace && (
          <OutlineButton
            style={{ marginTop: 10 }}
            title="Add Space Option"
            onClick={() => setAddingSpace(true)}
          />
        )}

        {addingSpace && (
          <EditSpace
            onSave={async newSpace => {
              const saved = await addSpace(newSpace);
              if (saved) setAddingSpace(false);
            }}
            onCancel={() => setAddingSpace(false)}
          />
        )}
      </Section>

      <BackButton onClick={back} />
      <NextButton primary={true} loading={loading} onClick={next} />
    </Wrapper>
  );
};

const mapState = (state: RootState, ownProps: RouteComponentProps) => {
  const { venueId } = ownProps.match.params as { venueId: string };

  return {
    venueEntity: selectors.getVenue(venueId)(state),
    loading: selectors.isUpdatingVenue(state)
  };
};
const mapDispatch = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      UpdateVenue
    },
    dispatch
  );
const connector = connect(mapState, mapDispatch);

export default connector(Spaces);
